import { API_BASE_URL, MEDIA_CONFIG } from "@/config";

// 🔹 Placeholder Images
export const PLACEHOLDER_ARTIST = "/public/placeholders/artist-placeholder.png";
export const PLACEHOLDER_ALBUM = "/public/placeholders/album-placeholder.png";

// 🔹 Helper: Get authentication token
const getAuthToken = () => localStorage.getItem("access_token");

// 🔹 Helper: Make authenticated API requests
const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const token = getAuthToken();

  const headers: HeadersInit = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error(`API Error: ${url} -`, errorData.error || `Status ${response.status}`);
    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json();
};

// ✅ Upload a media file (Supabase for images, Cloudflare R2 for audio)
export const uploadMedia = async (file: File, bucket: string, path: string = "", isPreview = false) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("bucket", bucket);
  formData.append("path", path);
  formData.append("isPreview", isPreview.toString());

  return fetchWithAuth(`${API_BASE_URL}/media/upload`, {
    method: "POST",
    body: formData,
  });
};

// ✅ Get signed URL for an image (from Supabase) with fallback
export const getSignedImageUrl = async (path: string, type: "artist" | "album") => {
  // Return placeholder immediately if path is empty or null
  if (!path) {
    console.warn(`No image path provided for ${type}, using placeholder`);
    return type === "artist" ? PLACEHOLDER_ARTIST : PLACEHOLDER_ALBUM;
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/media/image?path=${encodeURIComponent(path)}`);
    return response?.signedUrl || (type === "artist" ? PLACEHOLDER_ARTIST : PLACEHOLDER_ALBUM);
  } catch (error) {
    console.warn(`Failed to fetch signed image for ${type}:`, error);
    return type === "artist" ? PLACEHOLDER_ARTIST : PLACEHOLDER_ALBUM;
  }
};

// ✅ Get signed URL for preview audio (Cloudflare R2)
export const getSignedPreviewUrl = async (filePath: string) => {
  // Return null immediately if path is empty
  if (!filePath) {
    console.warn('No audio file path provided for preview');
    return null;
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/media/preview?filePath=${encodeURIComponent(filePath)}`);
    return response?.signedUrl || null;
  } catch (error) {
    console.warn("Failed to fetch signed preview URL:", error);
    return null;
  }
};

// ✅ Get signed URL for full audio file (requires authentication)
export const getSignedAudioUrl = async (filePath: string) => {
  // Return null immediately if path is empty
  if (!filePath) {
    console.warn('No audio file path provided');
    return null;
  }

  try {
    // Request JSON so API returns { signedUrl } for MassiveMusic (instead of 302 redirect)
    const response = await fetchWithAuth(`${API_BASE_URL}/media/audio?filePath=${encodeURIComponent(filePath)}`, {
      headers: { Accept: 'application/json' },
    });
    return response?.signedUrl || null;
  } catch (error) {
    console.warn("Failed to fetch signed audio URL:", error);
    return null;
  }
};

// ✅ Delete a media file
export const deleteMedia = async (bucket: string, path: string) => {
  return fetchWithAuth(`${API_BASE_URL}/media/delete`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bucket, path }),
  });
};

// ✅ List media files (future backend implementation required)
export const listMedia = async (bucket?: string) => {
  const queryParams = bucket ? `?bucket=${bucket}` : "";
  return fetchWithAuth(`${API_BASE_URL}/media/list${queryParams}`);
};

// ✅ Validate file type and size (Only for "image" and "audio")
export const validateFile = (file: File, type: "image" | "audio") => {
  if (type === "image") {
    if (!MEDIA_CONFIG.allowedImageTypes.includes(file.type)) {
      throw new Error(`Invalid file type. Allowed: ${MEDIA_CONFIG.allowedImageTypes.join(", ")}`);
    }
    if (file.size > MEDIA_CONFIG.maxImageSizeMB * 1024 * 1024) {
      throw new Error(`File too large. Max size: ${MEDIA_CONFIG.maxImageSizeMB}MB`);
    }
  } else if (type === "audio") {
    if (!MEDIA_CONFIG.allowedAudioTypes.includes(file.type)) {
      throw new Error(`Invalid file type. Allowed: ${MEDIA_CONFIG.allowedAudioTypes.join(", ")}`);
    }
    if (file.size > MEDIA_CONFIG.maxAudioSizeMB * 1024 * 1024) {
      throw new Error(`File too large. Max size: ${MEDIA_CONFIG.maxAudioSizeMB}MB`);
    }
  } else {
    throw new Error(`Unsupported file type: ${type}`);
  }

  return true;
};

// ✅ Check user permission for a specific storage bucket
export const hasPermissionForBucket = (userRole: string | number | null, bucket: string): boolean => {
  if (!userRole) return false;

  const roleMap: Record<string, number> = {
    listener: 1,
    artist: 2,
    admin: 3,
    superadmin: 4,
  };

  const roleId = typeof userRole === "string" ? roleMap[userRole.toLowerCase()] || 0 : userRole;

  // Admin (3) and Superadmin (4) can access everything
  if (roleId >= 3) return true;

  // Artists (2) have access to artist-related buckets
  if (roleId === 2) return ["artist-profile", "audio", "album-artwork"].includes(bucket);

  // Regular users (1) can only access their profile pictures
  return roleId === 1 && bucket === "profile-photos";
};



